import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { 
  Users, UserPlus, Mail, Shield, Trash2, Clock, CheckCircle, 
  XCircle, Crown, UserCog, Eye, Loader2 
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ROLE_ICONS = {
  owner: <Crown className="w-4 h-4 text-yellow-400" />,
  admin: <Shield className="w-4 h-4 text-blue-400" />,
  member: <UserCog className="w-4 h-4 text-green-400" />,
  viewer: <Eye className="w-4 h-4 text-gray-400" />
};

const ROLE_COLORS = {
  owner: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  admin: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  member: "bg-green-500/20 text-green-400 border-green-500/30",
  viewer: "bg-gray-500/20 text-gray-400 border-gray-500/30"
};

const STATUS_BADGES = {
  active: { color: "bg-green-500/20 text-green-400", icon: <CheckCircle className="w-3 h-3" /> },
  pending: { color: "bg-yellow-500/20 text-yellow-400", icon: <Clock className="w-3 h-3" /> }
};

export default function TeamPage({ companyId, company }) {
  const [team, setTeam] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  
  // Invite dialog
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  
  // Edit dialog
  const [editMember, setEditMember] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [saving, setSaving] = useState(false);

  const loadTeam = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const [teamRes, rolesRes] = await Promise.all([
        api.get(`/companies/${companyId}/team`),
        api.get('/team/roles')
      ]);
      setTeam(teamRes.data.team || []);
      setUserRole(teamRes.data.your_role);
      setRoles(rolesRes.data.roles || []);
    } catch (err) {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    setInviting(true);
    try {
      const res = await api.post(`/companies/${companyId}/team/invite`, {
        email: inviteEmail.trim(),
        role: inviteRole
      });
      const emailSent = res.data.email_sent;
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-medium">Invitation sent to {inviteEmail}</span>
          {emailSent ? (
            <span className="text-sm opacity-90">Email notification sent</span>
          ) : (
            <span className="text-sm opacity-75">Email notifications not configured</span>
          )}
        </div>
      );
      setShowInvite(false);
      setInviteEmail("");
      setInviteRole("member");
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editMember) return;
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}/team/${editMember.member_id}`, {
        role: editRole
      });
      toast.success("Role updated");
      setEditMember(null);
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!confirm(`Remove ${member.email} from the team?`)) return;
    try {
      await api.delete(`/companies/${companyId}/team/${member.member_id}`);
      toast.success("Team member removed");
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove member");
    }
  };

  const canManageTeam = userRole === "owner" || userRole === "admin";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#e85d24]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Team</h1>
          <p className="text-muted-foreground mt-1">
            Manage who has access to {company?.name || "this company"}
          </p>
        </div>
        {canManageTeam && (
          <Button onClick={() => setShowInvite(true)} data-testid="invite-member-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Invite Member
          </Button>
        )}
      </div>

      {/* Team Members */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-[#e85d24]" />
            Team Members ({team.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {team.map((member) => (
              <div 
                key={member.member_id || member.user_id}
                className="flex items-center justify-between p-4 bg-[#141b2d] rounded-lg border border-border/50"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#e85d24]/20 flex items-center justify-center overflow-hidden">
                    {member.picture ? (
                      <img src={member.picture} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <span className="text-[#e85d24] font-semibold">
                        {(member.name || member.email || "?")[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">
                        {member.name || member.email}
                      </span>
                      {member.is_owner && (
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">
                          Owner
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[#6b7280] text-sm">{member.email}</span>
                      {member.status === "pending" && (
                        <Badge className={`${STATUS_BADGES.pending.color} text-xs`}>
                          {STATUS_BADGES.pending.icon}
                          <span className="ml-1">Pending</span>
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Badge className={`${ROLE_COLORS[member.role]} border`}>
                    {ROLE_ICONS[member.role]}
                    <span className="ml-1 capitalize">{member.role}</span>
                  </Badge>
                  
                  {canManageTeam && !member.is_owner && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditMember(member);
                          setEditRole(member.role);
                        }}
                        className="text-[#8b9299] hover:text-white"
                      >
                        <UserCog className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Role Permissions Card */}
      <Card className="bg-[#141b2d] border-border/50">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#e85d24]" />
            Role Permissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {roles.filter(r => r.code !== "owner").map((role) => (
              <div key={role.code} className="p-3 bg-[#1a2235] rounded-lg border border-border/30">
                <div className="flex items-center gap-2 mb-2">
                  {ROLE_ICONS[role.code]}
                  <span className="text-white font-medium capitalize">{role.label}</span>
                </div>
                <p className="text-xs text-[#6b7280]">{role.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#e85d24]" />
              Invite Team Member
            </DialogTitle>
            <DialogDescription>
              Send an invitation to join {company?.name || "this company"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
                <Input
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="pl-10 bg-[#141b2d] border-border"
                  data-testid="invite-email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="bg-[#141b2d] border-border" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {roles.filter(r => r.code !== "owner").map((role) => (
                    <SelectItem key={role.code} value={role.code}>
                      <div className="flex items-center gap-2">
                        {ROLE_ICONS[role.code]}
                        <span>{role.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting} data-testid="send-invite-btn">
              {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editMember} onOpenChange={(open) => !open && setEditMember(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Update role for {editMember?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Select value={editRole} onValueChange={setEditRole}>
              <SelectTrigger className="bg-[#141b2d] border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {roles.filter(r => r.code !== "owner").map((role) => (
                  <SelectItem key={role.code} value={role.code}>
                    <div className="flex items-center gap-2">
                      {ROLE_ICONS[role.code]}
                      <span>{role.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMember(null)}>Cancel</Button>
            <Button onClick={handleUpdateRole} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
